import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import config from '../../config';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';
import StatusBadge from './StatusBadge';

function SchoolOrdersTab({ orders, loading, error, onOrderClick, getTotalNoteCount, handleOpenNotesDialog }) {
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentModelFilter, setPaymentModelFilter] = useState('all');

  // Fetch organizations to get proper school names
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/api/organizations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // The API returns { success: true, data: [...], pagination: {...} }
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          setOrganizations(response.data.data);
        } else if (Array.isArray(response.data)) {
          setOrganizations(response.data);
        } else {
          console.warn('Unexpected organizations response format:', response.data);
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      }
    };
    fetchOrganizations();
  }, []);

  // Enhanced school name getter to find organization name from organizationId
  const getSchoolName = (order) => {
    // If schoolName is directly available, use it
    if (order.schoolName && order.schoolName !== 'Unknown School') {
      return order.schoolName;
    }
    
    // Try to get school name from organizations list using organizationId
    if (order.organizationId && organizations.length > 0) {
      const org = organizations.find(org => org.id === order.organizationId);
      if (org) {
        return org.name;
      }
    }
    
    // Try to get organization name from notes (for legacy school orders)
    if (order.notes && typeof order.notes === 'string') {
      try {
        const notesData = JSON.parse(order.notes);
        if (notesData.schoolInfo && notesData.schoolInfo.name) {
          return notesData.schoolInfo.name;
        }
      } catch (e) {
        // Parsing failed, continue with other methods
      }
    }
    
    // Fallback to business name or default
    return order.businessName || 'Unknown School';
  };

  const getContactPerson = (order) => {
    // Check for firstName and lastName fields first (new schema)
    if (order.firstName || order.lastName) {
      return `${order.firstName || ''} ${order.lastName || ''}`.trim();
    }
    
    // Check for contactName field (for compatibility)
    if (order.contactName) {
      return order.contactName;
    }
    
    // Try to get contact name from notes
    if (order.notes && typeof order.notes === 'string') {
      try {
        const notesData = JSON.parse(order.notes);
        if (notesData.submittedBy) {
          // Check for firstName and lastName in notes
          if (notesData.submittedBy.firstName || notesData.submittedBy.lastName) {
            return `${notesData.submittedBy.firstName || ''} ${notesData.submittedBy.lastName || ''}`.trim();
          }
          
          // Fall back to name field if available
          if (notesData.submittedBy.name) {
            return notesData.submittedBy.name;
          }
        }
      } catch (e) {
        // Parsing failed, continue with other methods
      }
    }
    
    // Fallback to contactPerson (legacy field) or default
    return order.contactPerson || 'N/A';
  };

  const getFamiliesCount = (order) => {
    return order.numberOfFamilies || 'N/A';
  };

  const getIssuesPerTerm = (order) => {
    return order.issuesPerTerm || 'N/A';
  };

  const getPaymentModel = (order) => {
    return order.paymentModel || 'N/A';
  };

  const getProductsOrdered = (order) => {
    if (!order.productsOrdered) return 'N/A';
    
    if (typeof order.productsOrdered === 'string') {
      return order.productsOrdered || 'N/A';
    }
    
    if (Array.isArray(order.productsOrdered)) {
      return order.productsOrdered.join(', ') || 'N/A';
    }
    
    // Handle object format with boolean values
    if (typeof order.productsOrdered === 'object' && !Array.isArray(order.productsOrdered)) {
      const products = [];
      if (order.productsOrdered.schoolLink) products.push('SchoolLink');
      if (order.productsOrdered.ePublisher) products.push('ePublisher');
      if (order.productsOrdered.paper) products.push('Paper');
      return products.length > 0 ? products.join(', ') : 'N/A';
    }
    
    return 'N/A';
  };

  const getItemsCount = (order) => {
    if (!order.productsOrdered) return 0;
    
    if (typeof order.productsOrdered === 'string') {
      return order.productsOrdered.split(',').length;
    }
    
    if (Array.isArray(order.productsOrdered)) {
      return order.productsOrdered.length;
    }
    
    // Handle object format with boolean values
    if (typeof order.productsOrdered === 'object' && !Array.isArray(order.productsOrdered)) {
      let count = 0;
      if (order.productsOrdered.schoolLink) count++;
      if (order.productsOrdered.ePublisher) count++;
      if (order.productsOrdered.paper) count++;
      return count;
    }
    
    return 0;
  };

  const getTotalValue = (order) => {
    // Use totalPrice first, then fall back to value
    const price = order.totalPrice || order.total_price || order.value || 0;
    return parseFloat(price).toFixed(2);
  };

  // Extract the actual ID from the order object
  const getOrderId = (order) => {
    // Log the full order for debugging
    
    // Simple prioritization for ID fields
    if (order.id != null && order.id !== '' && order.id !== 'null' && order.id !== 'undefined') {
      return order.id;
    }
    
    if (order.orderId != null && order.orderId !== '' && order.orderId !== 'null' && order.orderId !== 'undefined') {
      return order.orderId;
    }
    
    if (order.schoolOrderId != null && order.schoolOrderId !== '' && order.schoolOrderId !== 'null' && order.schoolOrderId !== 'undefined') {
      return order.schoolOrderId;
    }
    
    // No valid ID found
    console.warn('No valid ID found for school order:', order);
    return 'New Order';
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle payment model filter change
  const handlePaymentModelChange = (event) => {
    setPaymentModelFilter(event.target.value);
  };

  // Filter orders based on search query and payment model filter
  const filteredOrders = orders.filter(order => {
    // First filter by payment model if specified
    if (paymentModelFilter !== 'all') {
      const orderPaymentModel = getPaymentModel(order);
      if (orderPaymentModel !== paymentModelFilter) {
        return false;
      }
    }
    
    // If no search query, return all orders that match the payment model filter
    if (!searchQuery) {
      return true;
    }
    
    const query = searchQuery.toLowerCase();
    const schoolName = getSchoolName(order).toLowerCase();
    const contactPerson = getContactPerson(order).toLowerCase();
    const productsOrdered = getProductsOrdered(order).toLowerCase();
    
    // Search in all relevant fields
    return (
      schoolName.includes(query) ||
      contactPerson.includes(query) ||
      productsOrdered.includes(query)
    );
  });

  // Get unique payment models for the filter dropdown
  const paymentModels = ['all', ...new Set(orders
    .map(order => getPaymentModel(order))
    .filter(model => model && model !== 'N/A'))];

  const handleOrderClick = (order) => {
    if (onOrderClick) {
      // Ensure the order has a valid ID before passing it to the parent component
      const orderId = getOrderId(order);
      
      if (orderId !== null && orderId !== undefined && orderId !== 'New Order') {
        // Make sure the order object has an id property set and orderType is 'school'
        const orderWithId = {
          ...order,
          id: orderId,
          orderId: orderId,        // Set both id and orderId for consistency
          schoolOrderId: orderId,  // Set schoolOrderId too for completeness
          orderType: 'school'      // Explicitly set the orderType to 'school'
        };
        
        // If organization ID is available, enrich the order with organization data
        if (order.organizationId && organizations.length > 0) {
          const org = organizations.find(org => org.id === order.organizationId);
          if (org) {
            orderWithId.organizationData = org;
          }
        }
        
        // If contact information is in notes, extract and add it directly to the order object
        if (order.notes && typeof order.notes === 'string') {
          try {
            const notesData = JSON.parse(order.notes);
            if (notesData.submittedBy) {
              // Add contact information directly to the order
              if (notesData.submittedBy.firstName && !orderWithId.firstName) {
                orderWithId.firstName = notesData.submittedBy.firstName;
              }
              if (notesData.submittedBy.lastName && !orderWithId.lastName) {
                orderWithId.lastName = notesData.submittedBy.lastName;
              }
              if (notesData.submittedBy.position && !orderWithId.position) {
                orderWithId.position = notesData.submittedBy.position;
              }
              if (notesData.submittedBy.email && !orderWithId.email) {
                orderWithId.email = notesData.submittedBy.email;
              }
              if (notesData.submittedBy.phone && !orderWithId.phone) {
                orderWithId.phone = notesData.submittedBy.phone;
              }
            }
          } catch (e) {
            console.error('Error extracting contact information from notes:', e);
          }
        }
        
        onOrderClick(orderWithId);
      } else {
        console.error('School order is missing a valid ID. Original order:', order);
        // Create a temporary alert to notify the user
        alert('This order has no ID and cannot be opened. Please contact support.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Filters and search row */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        {/* Payment model filter */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="payment-model-filter-label">Payment Model</InputLabel>
            <Select
              labelId="payment-model-filter-label"
              id="payment-model-filter"
              value={paymentModelFilter}
              onChange={handlePaymentModelChange}
              label="Payment Model"
            >
              {paymentModels.map(model => (
                <MenuItem key={model} value={model}>
                  {model === 'all' ? 'All Payment Models' : model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Search field */}
        <Grid item xs={12} sm={6} md={8} lg={9}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search by school name, contact person, or products..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Results count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredOrders.length} of {orders.length} orders
        </Typography>
      </Box>

      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="textSecondary">
            No school orders found
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Total Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Model</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order, index) => {
                const orderId = getOrderId(order);
                
                // Create a guaranteed unique key for this row
                const uniqueRowKey = orderId !== 'New Order' ? 
                  `school-order-${orderId}` : 
                  `school-order-${order.organizationId || 'unknown'}-${index}`;
                
                return (
                  <TableRow
                    key={uniqueRowKey}
                    hover
                    onClick={() => handleOrderClick(order)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {orderId}
                    </TableCell>
                    <TableCell>
                      {order.createdAt ? formatDateLong(order.createdAt) : 'Pending'}
                    </TableCell>
                    <TableCell>{getSchoolName(order)}</TableCell>
                    <TableCell>{getContactPerson(order)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        ${getTotalValue(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={order.status || 'New'} />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getPaymentModel(order)} 
                        size="small" 
                        variant="outlined"
                        color={getPaymentModel(order) === 'Ad Supported' ? 'success' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const products = getProductsOrdered(order);
                        if (products === 'N/A' || !products) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No products
                            </Typography>
                          );
                        }

                        // Get product color based on product type
                        const getProductColor = (product) => {
                          const productColors = {
                            'SchoolLink': 'success',
                            'ePublisher': 'primary', 
                            'Paper': 'warning'
                          };
                          return productColors[product] || 'default';
                        };

                        // Split products and create chips
                        const productList = products.split(',').map(p => p.trim());

                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 180 }}>
                            {productList.map((product, index) => (
                              <Chip
                                key={index}
                                label={product}
                                size="small"
                                variant="outlined"
                                color={getProductColor(product)}
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: 22,
                                  fontWeight: 500,
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 1)',
                                    transform: 'scale(1.02)'
                                  },
                                  transition: 'all 0.15s ease-in-out'
                                }}
                              />
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          const existingNotes = [];
                          if (order.notes) {
                            existingNotes.push({
                              id: 'general',
                              content: order.notes,
                              createdBy: 'Order Team',
                              createdAt: order.createdAt || new Date().toISOString(),
                              isNew: false,
                              noteType: 'general'
                            });
                          }
                          if (order.designNotes) {
                            existingNotes.push({
                              id: 'design',
                              content: order.designNotes,
                              createdBy: 'Design Team',
                              createdAt: order.createdAt || new Date().toISOString(),
                              isNew: false,
                              noteType: 'design'
                            });
                          }
                          handleOpenNotesDialog('order', order.id.toString(), getSchoolName(order), existingNotes);
                        }}
                        sx={{ 
                          minWidth: 'auto',
                          px: 1,
                          py: 0.5,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          borderRadius: 1,
                          color: (() => {
                            const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                            return totalCount > 0 ? 'primary.main' : 'text.secondary';
                          })(),
                          '&:hover': {
                            bgcolor: 'action.hover',
                          }
                        }}
                      >
                        Notes
                        {(() => {
                          const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                          return totalCount > 0 ? (
                            <Box
                              component="span"
                              sx={{
                                ml: 0.5,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                px: 0.5,
                                py: 0.1,
                                borderRadius: '50%',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                minWidth: 18,
                                height: 18,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {totalCount}
                            </Box>
                          ) : null;
                        })()}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

SchoolOrdersTab.propTypes = {
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onOrderClick: PropTypes.func,
  getTotalNoteCount: PropTypes.func,
  handleOpenNotesDialog: PropTypes.func
};

export default SchoolOrdersTab; 